import { MGPMap, MGPOptional, Set, MGPValidation, NumberMap, Utils } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { RulesConfigDescriptionLocalizable } from '../../config/RulesConfigDescriptionLocalizable';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { NInARowHelper } from '../../jscaip/NInARowHelper';
import { Orthogonal } from '../../jscaip/Orthogonal';
import { PlayerOrNone } from '../../jscaip/Player';
import { PlayerMap } from '../../jscaip/PlayerMap';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { MGPValidators } from '../../utils/MGPValidator';
import { QuixoFailure } from './QuixoFailure';
import { QuixoState } from './QuixoState';
export class QuixoNode extends GameNode {
}
export class QuixoRules extends ConfigurableRules {
    static singleton = MGPOptional.empty();
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Quixo`,
        config: {
            width: new NumberConfig(5, RulesConfigDescriptionLocalizable.WIDTH, MGPValidators.range(1, 99)),
            height: new NumberConfig(5, RulesConfigDescriptionLocalizable.HEIGHT, MGPValidators.range(1, 99)),
        },
    });
    static QUIXO_HELPER = new NInARowHelper(Utils.identity, 5);
    static getVerticalCoords(node) {
        const currentOpponent = node.gameState.getCurrentOpponent();
        const verticalCoords = [];
        const state = node.gameState;
        for (let y = 0; y < state.getHeight(); y++) {
            if (state.getPieceAtXY(0, y) !== currentOpponent) {
                verticalCoords.push(new Coord(0, y));
            }
            if (state.getPieceAtXY(state.getWidth() - 1, y) !== currentOpponent) {
                verticalCoords.push(new Coord(state.getWidth() - 1, y));
            }
        }
        return verticalCoords;
    }
    static getHorizontalCenterCoords(node) {
        const currentOpponent = node.gameState.getCurrentOpponent();
        const horizontalCenterCoords = [];
        const state = node.gameState;
        for (let x = 1; x < state.getWidth() - 1; x++) {
            if (state.getPieceAtXY(x, 0) !== currentOpponent) {
                horizontalCenterCoords.push(new Coord(x, 0));
            }
            if (state.getPieceAtXY(x, state.getHeight() - 1) !== currentOpponent) {
                horizontalCenterCoords.push(new Coord(x, state.getHeight() - 1));
            }
        }
        return horizontalCenterCoords;
    }
    static getLinesSums(state) {
        const sums = PlayerMap.ofValues(new MGPMap([
            { key: 'columns', value: new NumberMap() },
            { key: 'rows', value: new NumberMap() },
            { key: 'ascendingDiagonal', value: new NumberMap() },
            { key: 'descendingDiagonal', value: new NumberMap() },
        ]), new MGPMap([
            { key: 'columns', value: new NumberMap() },
            { key: 'rows', value: new NumberMap() },
            { key: 'ascendingDiagonal', value: new NumberMap() },
            { key: 'descendingDiagonal', value: new NumberMap() },
        ]));
        for (const coordAndContent of state.getPlayerCoordsAndContent()) {
            const content = coordAndContent.content;
            const x = coordAndContent.coord.x;
            const y = coordAndContent.coord.y;
            sums.get(content).get('columns').get().addOrSet(x, 1);
            sums.get(content).get('rows').get().addOrSet(y, 1);
            sums.get(content).get('ascendingDiagonal').get().addOrSet(x + y, 1);
            sums.get(content).get('descendingDiagonal').get().addOrSet(x - y, 1);
        }
        return sums;
    }
    static getVictoriousCoords(state) {
        const victoriousCoord = QuixoRules.QUIXO_HELPER.getVictoriousCoord(state);
        const opponentCoords = QuixoRules.getPlayersCoords(victoriousCoord, state, state.getPreviousOpponent());
        const playerCoords = QuixoRules.getPlayersCoords(victoriousCoord, state, state.getPreviousPlayer());
        if (opponentCoords.length === 0) {
            if (playerCoords.length === 0) {
                return []; // Nobody won
            }
            else {
                return playerCoords; // Player won
            }
        }
        else {
            // if there is no player coords, then opponent won
            // if there is both player coords,
            // Then player made opponent win by making two victories
            return opponentCoords;
        }
    }
    static getPlayersCoords(coords, state, player) {
        return coords.filter((coord) => {
            return state.getPieceAt(coord).equals(player);
        });
    }
    static getFullestLine(playerLinesInfo) {
        let linesScores = playerLinesInfo.get('columns').get().getValueList();
        linesScores = linesScores.concat(playerLinesInfo.get('rows').get().getValueList());
        linesScores = linesScores.concat(playerLinesInfo.get('ascendingDiagonal').get().getValueList());
        linesScores = linesScores.concat(playerLinesInfo.get('descendingDiagonal').get().getValueList());
        return Math.max(...linesScores);
    }
    static get() {
        if (QuixoRules.singleton.isAbsent()) {
            QuixoRules.singleton = MGPOptional.of(new QuixoRules());
        }
        return QuixoRules.singleton.get();
    }
    getRulesConfigDescription() {
        return QuixoRules.RULES_CONFIG_DESCRIPTION;
    }
    getInitialState(config) {
        const initialBoard = TableUtils.create(config.width, config.height, PlayerOrNone.NONE);
        return new QuixoState(initialBoard, 0);
    }
    getPossibleDirections(state, coord) {
        const possibleDirections = [];
        if (coord.x !== 0)
            possibleDirections.push(Orthogonal.LEFT);
        if (coord.y !== 0)
            possibleDirections.push(Orthogonal.UP);
        if (coord.x !== (state.getWidth() - 1))
            possibleDirections.push(Orthogonal.RIGHT);
        if (coord.y !== (state.getHeight() - 1))
            possibleDirections.push(Orthogonal.DOWN);
        return possibleDirections;
    }
    isValidCoord(state, coord) {
        Utils.assert(state.isOnBoard(coord), 'Invalid coord for QuixoMove: ' + coord.toString() + ' is outside the board.');
        if (coord.x !== 0 &&
            coord.x !== (state.getWidth() - 1) &&
            coord.y !== 0 &&
            coord.y !== (state.getHeight() - 1)) {
            return MGPValidation.failure(QuixoFailure.NO_INSIDE_CLICK());
        }
        return MGPValidation.SUCCESS;
    }
    assertDirectionValidity(move, state) {
        const x = move.coord.x;
        const y = move.coord.y;
        const direction = move.direction;
        Utils.assert(x !== (state.getWidth() - 1) || direction !== Orthogonal.RIGHT, `Invalid direction: piece on the right side can't be moved to the right.`);
        Utils.assert(y !== (state.getHeight() - 1) || direction !== Orthogonal.DOWN, `Invalid direction: piece on the bottom side can't be moved down.`);
        Utils.assert(x !== 0 || direction !== Orthogonal.LEFT, `Invalid direction: piece on the left side can't be moved to the left.`);
        Utils.assert(y !== 0 || direction !== Orthogonal.UP, `Invalid direction: piece on the top side can't be moved up.`);
    }
    applyLegalMove(move, state, _config, _info) {
        return state.applyLegalMove(move);
    }
    isLegal(move, state) {
        const coordValidity = this.isValidCoord(state, move.coord);
        if (coordValidity.isFailure()) {
            return coordValidity;
        }
        this.assertDirectionValidity(move, state);
        if (state.getPieceAt(move.coord) === state.getCurrentOpponent()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        else {
            return MGPValidation.SUCCESS;
        }
    }
    getGameStatus(node) {
        const state = node.gameState;
        const victoriousCoord = QuixoRules.QUIXO_HELPER.getVictoriousCoord(state);
        const unreducedWinners = victoriousCoord.map((coord) => state.getPieceAt(coord));
        const winners = new Set(unreducedWinners);
        if (winners.size() === 0) {
            return GameStatus.ONGOING;
        }
        else if (winners.size() === 1) {
            return GameStatus.getVictory(winners.getAnyElement().get());
        }
        else {
            return GameStatus.getVictory(state.getCurrentPlayer());
        }
    }
}
